/**
 * MiniMap — Canvas navigator
 * Per PLAN.md ui/MiniMap.js and PRD §4.2
 */

import { eventBus, Events } from '../core/EventBus.js';

export class MiniMap {
    /**
     * @param {HTMLElement} container 
     * @param {import('../core/Canvas.js').Canvas} canvas 
     */
    constructor(container, canvas) {
        this.container = container;
        this.canvas = canvas;
        this.element = null;
        this.mapCanvas = null;
        this.ctx = null;

        this.width = 150;
        this.height = 100;
        this.scale = 0.02; // World to minimap scale

        this.init();
    }

    init() {
        this.element = document.createElement('div');
        this.element.className = 'minimap';
        this.element.style.width = `${this.width}px`;
        this.element.style.height = `${this.height}px`;

        this.mapCanvas = document.createElement('canvas');
        this.mapCanvas.width = this.width * 2;
        this.mapCanvas.height = this.height * 2;
        this.mapCanvas.style.width = `${this.width}px`;
        this.mapCanvas.style.height = `${this.height}px`;

        this.ctx = this.mapCanvas.getContext('2d');
        this.ctx.scale(2, 2); // High DPI

        this.viewportIndicator = document.createElement('div');
        this.viewportIndicator.className = 'minimap-viewport';

        this.element.appendChild(this.mapCanvas);
        this.element.appendChild(this.viewportIndicator);
        this.container.appendChild(this.element);

        this.bindEvents();
    }

    bindEvents() {
        // Click to navigate
        this.element.addEventListener('click', (e) => {
            const rect = this.element.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            // Convert to world coordinates
            const worldX = (x - this.width / 2) / this.scale;
            const worldY = (y - this.height / 2) / this.scale;

            // Pan to this location
            this.canvas.viewport.x = worldX;
            this.canvas.viewport.y = worldY;
            eventBus.emit(Events.RENDER_REQUEST);
        });

        // Throttled update on render - use rAF to batch updates
        let renderPending = false;
        eventBus.on(Events.RENDER_REQUEST, () => {
            if (!renderPending) {
                renderPending = true;
                requestAnimationFrame(() => {
                    this.render();
                    renderPending = false;
                });
            }
        });
    }

    /**
     * Render minimap
     */
    render() {
        const { ctx, width, height, scale } = this;
        const viewport = this.canvas.viewport;
        const isDark = this.canvas.isDark;

        // Clear
        ctx.fillStyle = isDark ? '#1E1E1E' : '#FFFFFF';
        ctx.fillRect(0, 0, width, height);

        // Draw nodes as dots
        if (this.canvas.document) {
            const nodes = this.canvas.document.getAllNodes();

            for (const node of nodes) {
                const x = width / 2 + node.position.x * scale;
                const y = height / 2 + node.position.y * scale;

                // Skip if off minimap
                if (x < 0 || x > width || y < 0 || y > height) continue;

                ctx.fillStyle = isDark ? '#777' : '#888';
                ctx.beginPath();
                ctx.arc(x, y, 2, 0, Math.PI * 2);
                ctx.fill();
            }

            // Draw connections as lines
            const connections = this.canvas.document.getAllConnections();
            ctx.strokeStyle = isDark ? '#555' : '#ccc';
            ctx.lineWidth = 0.5;

            for (const conn of connections) {
                const source = this.canvas.document.getNode(conn.sourceId);
                const target = this.canvas.document.getNode(conn.targetId);

                if (!source || !target) continue;

                const x1 = width / 2 + source.position.x * scale;
                const y1 = height / 2 + source.position.y * scale;
                const x2 = width / 2 + target.position.x * scale;
                const y2 = height / 2 + target.position.y * scale;

                ctx.beginPath();
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.stroke();
            }
        }

        // Update viewport indicator
        const viewportWidth = viewport.width / viewport.zoom * scale;
        const viewportHeight = viewport.height / viewport.zoom * scale;
        const viewportX = width / 2 + (viewport.x - viewport.width / 2 / viewport.zoom) * scale;
        const viewportY = height / 2 + (viewport.y - viewport.height / 2 / viewport.zoom) * scale;

        this.viewportIndicator.style.left = `${viewportX}px`;
        this.viewportIndicator.style.top = `${viewportY}px`;
        this.viewportIndicator.style.width = `${Math.max(10, viewportWidth)}px`;
        this.viewportIndicator.style.height = `${Math.max(10, viewportHeight)}px`;
    }

    /**
     * Show/hide minimap
     * @param {boolean} visible 
     */
    setVisible(visible) {
        this.element.style.display = visible ? 'block' : 'none';
    }

    /**
     * Destroy minimap
     */
    destroy() {
        this.element.remove();
    }
}
